import React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { SquareArrowOutUpRight, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  width?: string;
  height?: string;
  link?: string;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  title,
  width = "max-w-lg",
  height = "max-h-[80vh]",
  link,
}) => {
  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <AnimatePresence>
        {isOpen && (
          <Dialog.Portal>
            {/* Overlay com animação de fade */}
            <Dialog.Overlay asChild>
              <motion.div
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              />
            </Dialog.Overlay>

            {/* Modal Content com animação de zoom suave */}
            <Dialog.Content asChild>
              <motion.div
                className={`fixed top-[50%] left-[50%] transform -translate-x-1/2 -translate-y-1/2 
                                bg-gradient-to-b from-gray-800/80 to-gray-900/80 rounded-xl shadow-2xl w-[95%] sm:w-full ${width} 
                                max-h-[90vh] sm:${height} flex flex-col z-50 border border-red-500/20 backdrop-blur-sm overflow-hidden`}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-red-600/5 via-red-600/5 to-transparent opacity-50"></div>
                <div className="absolute top-0 right-0 w-64 h-64 bg-red-500/5 rounded-full filter blur-3xl -translate-y-1/2 translate-x-1/2"></div>
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-red-500/5 rounded-full filter blur-3xl translate-y-1/2 -translate-x-1/2"></div>
                {/* Header fixo */}
                <div className="flex relative justify-between items-center p-6 z-10">
                  {title && (
                    <div className="flex items-center gap-3">
                      <Dialog.Title className="text-xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                        {title}
                      </Dialog.Title>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    {link && (
                      <Link
                        to={link}
                        className="text-gray-400 hover:text-red-400 transition-all hover:bg-gray-800/80 p-1.5 rounded-full border border-gray-700/50 hover:border-red-500/30"
                      >
                        <SquareArrowOutUpRight size={18} />
                      </Link>
                    )}
                    <Dialog.Close asChild>
                      <motion.button
                        onClick={onClose}
                        className="text-gray-400 hover:text-red-400 transition-all hover:bg-gray-800/80 p-1.5 rounded-full border border-gray-700/50 hover:border-red-500/30"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <X size={18} />
                      </motion.button>
                    </Dialog.Close>
                  </div>
                </div>
                <div className="h-0.5 w-32 bg-gradient-to-r from-red-500/30 to-transparent mx-6 mb-6 relative z-10"></div>

                {/* Conteúdo do Modal com rolagem interna */}
                <div className="flex-grow overflow-y-auto px-6 pb-6 scrollbar-custom max-h-[60vh] relative z-10">
                  <div className="bg-gradient-to-b from-gray-800/40 to-gray-900/40 rounded-xl overflow-hidden border border-gray-700/50 hover:border-red-500/30 transition-all duration-300 shadow-inner p-4 text-gray-300">
                    {children}
                  </div>
                </div>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
};

export default Modal;
